import { ChecklistItem, EvidenceFieldDefinition } from '@/firebase/checklistService';
import { ChecklistItemResult } from '@/firebase/checklistCompletionService';
import { ChecklistCompletion } from '@/firebase/checklistCompletionService';
import { AssetFact } from '@/firebase/assetFactsService';

/**
 * Auto-complete rule for checklist items
 * Automatically sets status based on fact conditions
 */
export interface AutoCompleteRule {
  condition: string; // JavaScript expression evaluated against facts, e.g., "flood.reconTriggered === false"
  status: 'PASS' | 'NA';
  reason: string; // Explanation for the status
}

/**
 * Extended checklist item with auto-complete rules
 * Fields are now only defined in evidenceDefinitions.fieldDefinitions
 */
export interface ExtendedChecklistItem extends ChecklistItem {
  autoCompleteRules?: AutoCompleteRule[];
  confidenceThreshold?: number; // Default confidence threshold for this item (0-1, default 0.8)
}

/**
 * Validation result for a checklist item
 */
export interface ItemValidationResult {
  isValid: boolean;
  status: 'COMPLETE' | 'INCOMPLETE' | 'NEEDS_REVIEW';
  missingFields: string[]; // Keys of missing required fields
  invalidFields: Array<{
    key: string;
    reason: string;
  }>;
  lowConfidenceFields: Array<{
    key: string;
    confidence: number;
    threshold: number;
  }>;
  suggestedStatus?: 'PASS' | 'FAIL' | 'NA' | 'NEEDS_REVIEW';
  suggestedReason?: string;
}

/**
 * Get field value from various sources
 * Uses the same logic as the display to ensure consistency
 */
function getFieldValue(
  fieldKey: string,
  itemResult: ChecklistItemResult | undefined,
  facts: Record<string, AssetFact>,
  evidenceFields?: Record<string, any>
): any {
  // Try evidence fields first (most specific)
  if (evidenceFields && evidenceFields[fieldKey] !== undefined && evidenceFields[fieldKey] !== null && evidenceFields[fieldKey] !== '') {
    return evidenceFields[fieldKey];
  }
  
  // Try item result computed checks
  if (itemResult?.computedChecks) {
    const computedKey = fieldKey.replace(/\./g, '_');
    if ((itemResult.computedChecks as any)[computedKey] !== undefined) {
      return (itemResult.computedChecks as any)[computedKey];
    }
  }
  
  // Try facts with exact key match first
  let fact = facts[fieldKey];
  if (fact && fact.value !== null && fact.value !== undefined && fact.value !== '') {
    return fact.value;
  }
  
  // Try fuzzy key matching for asset facts (case-insensitive, whitespace-removed, etc.)
  const factKeys = Object.keys(facts);
  const matchingKey = factKeys.find(k => 
    k.toLowerCase() === fieldKey.toLowerCase() ||
    k.replace(/\s+/g, '') === fieldKey.replace(/\s+/g, '') ||
    k.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() === fieldKey.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
  );
  
  if (matchingKey && matchingKey !== fieldKey) {
    fact = facts[matchingKey];
    if (fact && fact.value !== null && fact.value !== undefined && fact.value !== '') {
      return fact.value;
    }
  }
  
  return undefined;
}


/**
 * Evaluate auto-complete rules
 */
export function evaluateAutoCompleteRules(
  item: ExtendedChecklistItem,
  facts: Record<string, AssetFact>
): { status: 'PASS' | 'NA'; reason?: string } | null {
  if (!item.autoCompleteRules || item.autoCompleteRules.length === 0) {
    return null;
  }
  
  // Create a safe evaluation context from facts
  const context: Record<string, any> = {};
  Object.entries(facts).forEach(([key, fact]) => {
    // Convert dot notation to nested object
    const keys = key.split('.');
    let current = context;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = fact.value;
  });
  
  // Try each auto-complete rule
  for (const rule of item.autoCompleteRules) {
    try {
      // Simple evaluation - in production, use a safer evaluator
      // For now, we'll do basic fact matching
      const condition = rule.condition;
      
      // Check if condition matches facts
      // This is a simplified version - in production, use a proper expression evaluator
      let matches = false;
      
      // Try to match common patterns
      if (condition.includes('===')) {
        const [left, right] = condition.split('===').map(s => s.trim());
        const leftValue = getFieldValue(left.replace(/['"]/g, ''), undefined, facts);
        const rightValue = right.replace(/['"]/g, '');
        
        if (typeof leftValue === 'boolean') {
          matches = leftValue === (rightValue === 'true');
        } else if (typeof leftValue === 'string') {
          matches = leftValue === rightValue;
        } else {
          matches = String(leftValue) === rightValue;
        }
      } else if (condition.includes('==')) {
        const [left, right] = condition.split('==').map(s => s.trim());
        const leftValue = getFieldValue(left.replace(/['"]/g, ''), undefined, facts);
        const rightValue = right.replace(/['"]/g, '');
        matches = String(leftValue) === String(rightValue);
      }
      
      if (matches && rule.status) {
        return { status: rule.status, reason: rule.reason };
      }
    } catch (error) {
      console.error(`Error evaluating auto-complete rule: ${rule.condition}`, error);
    }
  }
  
  return null;
}

/**
 * Collect all fields from evidenceDefinitions for validation
 */
function collectFieldsFromEvidenceDefinitions(item: ExtendedChecklistItem): EvidenceFieldDefinition[] {
  const allFields: EvidenceFieldDefinition[] = [];
  
  if (!item.evidenceDefinitions || item.evidenceDefinitions.length === 0) {
    return allFields;
  }
  
  for (const evidenceDef of item.evidenceDefinitions) {
    if (evidenceDef.fieldDefinitions && evidenceDef.fieldDefinitions.length > 0) {
      allFields.push(...evidenceDef.fieldDefinitions);
    }
  }
  
  return allFields;
}

/**
 * Validate a checklist item completion
 * Now uses only fields from evidenceDefinitions.fieldDefinitions
 */
export function validateItemCompletion(
  item: ExtendedChecklistItem,
  itemResult: ChecklistItemResult | undefined,
  facts: Record<string, AssetFact>,
  evidenceFields?: Record<string, any>
): ItemValidationResult {
  const result: ItemValidationResult = {
    isValid: true,
    status: 'COMPLETE',
    missingFields: [],
    invalidFields: [],
    lowConfidenceFields: [],
  };
  
  // Check auto-complete rules first
  const autoComplete = evaluateAutoCompleteRules(item, facts);
  if (autoComplete) {
    result.suggestedStatus = autoComplete.status;
    result.suggestedReason = autoComplete.reason;
  }
  
  // Collect all fields from evidenceDefinitions
  const allFields = collectFieldsFromEvidenceDefinitions(item);
  
  // If no fields defined, item is valid if it has a status
  if (allFields.length === 0) {
    return result;
  }
  
  const currentStatus = itemResult?.status;
  const confidenceThreshold = item.confidenceThreshold || 0.8;
  
  // Validate each field
  for (const field of allFields) {
    // Check if field is required
    // Field is required if:
    // 1. Field itself is marked as required, AND
    // 2. The evidence definition is required (or field is required even if evidence is optional)
    const evidenceDef = item.evidenceDefinitions?.find(ed => 
      ed.fieldDefinitions?.some(fd => fd.key === field.key)
    );
    const isRequired = field.required === true && (evidenceDef?.required === true || field.required === true);
    
    if (!isRequired) {
      continue; // Skip optional fields
    }
    
    const value = getFieldValue(field.key, itemResult, facts, evidenceFields);
    
    // Check if field is missing
    if (value === undefined || value === null || value === '') {
      result.missingFields.push(field.key);
      result.isValid = false;
      result.status = 'INCOMPLETE';
      
      // Debug logging for missing required fields
      const factKeys = Object.keys(facts);
      const matchingKey = factKeys.find(k => 
        k.toLowerCase() === field.key.toLowerCase() ||
        k.replace(/\s+/g, '') === field.key.replace(/\s+/g, '') ||
        k.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() === field.key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
      );
      if (matchingKey && matchingKey !== field.key) {
        console.warn(`[Validation] Field "${field.label}" key mismatch: looking for "${field.key}" but found "${matchingKey}" in asset facts`);
      } else if (!facts[field.key] && factKeys.length > 0) {
        console.warn(`[Validation] Required field "${field.label}" (key: "${field.key}") not found. Available fact keys:`, factKeys.slice(0, 10));
      } else if (facts[field.key] && (facts[field.key].value === null || facts[field.key].value === undefined || facts[field.key].value === '')) {
        console.warn(`[Validation] Field "${field.label}" (key: "${field.key}") found in asset facts but value is empty/null`);
      }
      if (evidenceFields && Object.keys(evidenceFields).length > 0) {
        console.warn(`[Validation] Evidence fields available:`, Object.keys(evidenceFields));
      }
      continue;
    }
    
    // Validate field value
    const validation = validateFieldValueFromEvidenceField(field, value, facts, itemResult);
    if (!validation.valid) {
      result.invalidFields.push({
        key: field.key,
        reason: validation.reason || 'Invalid value',
      });
      result.isValid = false;
      result.status = 'INCOMPLETE';
    }
    
    // Check confidence if field was extracted (from asset facts)
    const fact = facts[field.key];
    if (fact && fact.confidence !== undefined) {
      // Convert confidence string to number for comparison
      const confidenceMap: Record<string, number> = { 'high': 0.9, 'medium': 0.7, 'low': 0.5 };
      const confidenceValue = confidenceMap[fact.confidence] || 0.5;
      if (confidenceValue < confidenceThreshold) {
        result.lowConfidenceFields.push({
          key: field.key,
          confidence: confidenceValue,
          threshold: confidenceThreshold,
        });
        result.status = 'NEEDS_REVIEW';
      }
    }
  }
  
  // If there are low confidence fields, mark as needs review
  if (result.lowConfidenceFields.length > 0 && result.status === 'COMPLETE') {
    result.status = 'NEEDS_REVIEW';
  }
  
  return result;
}

/**
 * Normalize user-friendly yes/no (and true/false) to boolean for validation.
 * Returns null if value is not a recognized boolean-like value.
 */
function normalizeBooleanForValidation(value: any): boolean | null {
  if (value === true || value === false) return value;
  if (value === undefined || value === null) return null;
  const s = String(value).trim().toLowerCase();
  if (s === 'true' || s === 'yes' || s === '1') return true;
  if (s === 'false' || s === 'no' || s === '0') return false;
  return null;
}

/**
 * Validate a field value from EvidenceFieldDefinition
 */
export function validateFieldValueFromEvidenceField(
  field: EvidenceFieldDefinition,
  value: any,
  facts: Record<string, AssetFact>,
  itemResult?: ChecklistItemResult
): { valid: boolean; reason?: string } {
  // Unwrap evidence-style value (e.g. { value: "...", source: "...", confidence: "..." })
  if (value !== undefined && value !== null && typeof value === 'object' && !Array.isArray(value) && Object.prototype.hasOwnProperty.call(value, 'value')) {
    value = (value as { value: any }).value;
  }

  if (field.required && (value === undefined || value === null || value === '')) {
    return { valid: false, reason: `${field.label} is required` };
  }
  
  if (value === undefined || value === null || value === '') {
    return { valid: true }; // Optional field, no value is OK
  }
  
  // Type validation
  if (field.type === 'number' && typeof value !== 'number') {
    return { valid: false, reason: `${field.label} must be a number` };
  }
  
  if (field.type === 'boolean') {
    const normalized = normalizeBooleanForValidation(value);
    if (normalized === null) {
      return { valid: false, reason: `${field.label} must be true or false` };
    }
    // Accept: true, false, "yes", "no", "true", "false" (case-insensitive)
  }
  
  if (field.type === 'date') {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
      return { valid: false, reason: `${field.label} must be a valid date` };
    }
  }
  
  if (field.type === 'enum' && field.enumValues) {
    if (!field.enumValues.includes(value)) {
      return { valid: false, reason: `${field.label} must be one of: ${field.enumValues.join(', ')}` };
    }
  }

  if (field.type === 'list') {
    // Accept array, comma-separated string (display as-is in GUI), or evidence-style - validation passes if there is content
    let listValue: unknown[] = [];
    if (Array.isArray(value)) {
      listValue = value;
    } else if (typeof value === 'string' && value.trim() !== '') {
      listValue = [value.trim()];
    } else if (value !== null && value !== undefined && value !== '') {
      // Any other non-empty value (e.g. already unwrapped object): treat as valid so GUI comma-separated display doesn't fail
      listValue = [String(value)];
    }
    if (field.required && listValue.length === 0) {
      return { valid: false, reason: `${field.label} is required and must contain at least one item` };
    }
    if (listValue.length > 0) {
      const allStrings = listValue.every(item => item === null || item === undefined || typeof item === 'string');
      if (!allStrings) {
        return { valid: false, reason: `${field.label} must be a list of text values` };
      }
    }
  }

  // Validation rules
  if (field.validationRules) {
    const rules = field.validationRules;
    
    if (rules.mustBeLessThan) {
      const beforeValue = getFieldValue(rules.mustBeLessThan, itemResult, facts);
      if (beforeValue) {
        const beforeDate = beforeValue instanceof Date ? beforeValue : new Date(beforeValue);
        const thisDate = value instanceof Date ? value : new Date(value);
        if (thisDate >= beforeDate) {
          return { valid: false, reason: `${field.label} must be before ${rules.mustBeLessThan}` };
        }
      }
    }
    
    if (rules.mustBeGreaterThan) {
      const afterValue = getFieldValue(rules.mustBeGreaterThan, itemResult, facts);
      if (afterValue) {
        const afterDate = afterValue instanceof Date ? afterValue : new Date(afterValue);
        const thisDate = value instanceof Date ? value : new Date(value);
        if (thisDate <= afterDate) {
          return { valid: false, reason: `${field.label} must be after ${rules.mustBeGreaterThan}` };
        }
      }
    }
    
    if (rules.min !== undefined && typeof value === 'number' && value < rules.min) {
      return { valid: false, reason: `${field.label} must be at least ${rules.min}` };
    }
    
    if (rules.max !== undefined && typeof value === 'number' && value > rules.max) {
      return { valid: false, reason: `${field.label} must be at most ${rules.max}` };
    }
    
    if (rules.pattern) {
      const regex = new RegExp(rules.pattern);
      if (!regex.test(String(value))) {
        return { valid: false, reason: `${field.label} does not match required format` };
      }
    }
  }
  
  return { valid: true };
}

/**
 * Get missing required fields for an item
 * Now uses only fields from evidenceDefinitions
 */
export function getMissingFields(
  item: ExtendedChecklistItem,
  itemResult: ChecklistItemResult | undefined,
  facts: Record<string, AssetFact>,
  evidenceFields?: Record<string, any>
): EvidenceFieldDefinition[] {
  const allFields = collectFieldsFromEvidenceDefinitions(item);
  const missing: EvidenceFieldDefinition[] = [];
  
  for (const field of allFields) {
    const evidenceDef = item.evidenceDefinitions?.find(ed => 
      ed.fieldDefinitions?.some(fd => fd.key === field.key)
    );
    const isRequired = field.required === true && (evidenceDef?.required === true || field.required === true);
    
    if (isRequired) {
      const value = getFieldValue(field.key, itemResult, facts, evidenceFields);
      if (value === undefined || value === null || value === '') {
        missing.push(field);
      }
    }
  }
  
  return missing;
}

/**
 * Check if item can be marked as complete
 */
export function canMarkItemComplete(
  item: ExtendedChecklistItem,
  itemResult: ChecklistItemResult | undefined,
  facts: Record<string, AssetFact>,
  evidenceFields?: Record<string, any>
): { canComplete: boolean; reason?: string } {
  const validation = validateItemCompletion(item, itemResult, facts, evidenceFields);
  
  if (!validation.isValid) {
    const reasons: string[] = [];
    
    if (validation.missingFields.length > 0) {
      reasons.push(`Missing required fields: ${validation.missingFields.join(', ')}`);
    }
    
    if (validation.invalidFields.length > 0) {
      const invalidReasons = validation.invalidFields.map(f => `${f.key}: ${f.reason}`).join('; ');
      reasons.push(`Invalid fields: ${invalidReasons}`);
    }
    
    return {
      canComplete: false,
      reason: reasons.join('. '),
    };
  }
  
  if (validation.status === 'NEEDS_REVIEW' && validation.lowConfidenceFields.length > 0) {
    const fields = validation.lowConfidenceFields.map(f => f.key).join(', ');
    return {
      canComplete: false,
      reason: `Low confidence fields require reviewer confirmation: ${fields}`,
    };
  }
  
  return { canComplete: true };
}

